/**
 * Console capture for LOGS-01/LOGS-02 (D-11).
 *
 * Patches console.error and console.warn (NOT console.log — too noisy).
 * Also listens for window 'unhandledrejection' events (uncaught Promise rejections).
 *
 * Each captured entry: { ts, level: 'error'|'warn', message }.
 * Always calls through to the original console method — never silences logs.
 */
import type { ConsoleLogEntry } from '@support-system/shared';

type RecordFn = (entry: ConsoleLogEntry) => void;

/** Maximum message length to prevent enormous strings in the buffer */
const MAX_MESSAGE_LEN = 512;

function safeStringify(args: unknown[]): string {
    try {
        return args
            .map((a) => {
                if (typeof a === 'string') return a;
                if (a instanceof Error) return `${a.name}: ${a.message}`;
                try {
                    return JSON.stringify(a);
                } catch {
                    return String(a);
                }
            })
            .join(' ')
            .slice(0, MAX_MESSAGE_LEN);
    } catch {
        return '[unserializable]';
    }
}

/**
 * Install console (error/warn) and unhandledrejection capture.
 *
 * @param record - Called with each ConsoleLogEntry.
 * @returns A dispose function that restores originals and removes the event listener.
 */
export function installConsoleCapture(record: RecordFn): () => void {
    const originalError = console.error.bind(console);
    const originalWarn = console.warn.bind(console);

    console.error = (...args: unknown[]) => {
        record({ ts: Date.now(), level: 'error', message: safeStringify(args) });
        originalError(...args);
    };

    console.warn = (...args: unknown[]) => {
        record({ ts: Date.now(), level: 'warn', message: safeStringify(args) });
        originalWarn(...args);
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
        const reason = event.reason;
        const message =
            reason instanceof Error
                ? `Unhandled rejection: ${reason.name}: ${reason.message}`
                : `Unhandled rejection: ${String(reason)}`;
        record({ ts: Date.now(), level: 'error', message: message.slice(0, MAX_MESSAGE_LEN) });
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
        console.error = originalError;
        console.warn = originalWarn;
        window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
}
